import { CheckCircleOutlined, CloseCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { Alert, Badge, Button, Card, Col, Descriptions, Row, Space, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import aiService from '../../services/aiService';
import speechToTextService from '../../services/speechToTextService';

const { Title, Text } = Typography;

const AISettingsPage = () => {
  const [aiCapabilities, setAiCapabilities] = useState({});
  const [sttCapabilities, setSttCapabilities] = useState({});
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    checkCapabilities();
  }, []);

  const checkCapabilities = () => {
    setSttCapabilities(speechToTextService.getCapabilities());
    
    // Проверяем настройки AI
    const provider = process.env.REACT_APP_AI_PROVIDER;
    const openaiKey = process.env.REACT_APP_OPENAI_API_KEY;
    
    setAiCapabilities({
      provider: provider || 'не настроен',
      openaiConfigured: !!(openaiKey && openaiKey !== 'your_openai_api_key_here'),
      ollamaUrl: process.env.REACT_APP_OLLAMA_BASE_URL || 'http://localhost:11434'
    });
  };

  const testAIService = async () => {
    setTesting(true);
    const results = {};

    try {
      // Тест генерации петиции
      const testTranscript = "Это тестовое сообщение для проверки AI сервиса";
      const petition = await aiService.generatePetitionFromTranscript(testTranscript, 'ru');
      results.petitionGeneration = {
        success: true,
        title: petition.title,
        description: petition.description.slice(0, 100) + '...'
      };
    } catch (error) {
      results.petitionGeneration = {
        success: false,
        error: error.message
      };
    }

    try {
      // Тест модерации
      const moderation = await aiService.moderateContent("Тестовый контент для модерации");
      results.moderation = {
        success: true,
        is_safe: moderation.is_safe,
        issues: moderation.issues
      };
    } catch (error) {
      results.moderation = {
        success: false,
        error: error.message
      };
    }

    setTestResults(results);
    setTesting(false);
  };

  const StatusIcon = ({ success }) => 
    success ? 
      <CheckCircleOutlined style={{ color: '#52c41a' }} /> : 
      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Title level={2}>
        <SettingOutlined /> Настройки AI/LLM
      </Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Speech-to-Text возможности" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item 
                label="Web Speech API"
                span={1}
              >
                <Space>
                  <StatusIcon success={sttCapabilities.webSpeechAPI} />
                  <Text>{sttCapabilities.webSpeechAPI ? 'Поддерживается' : 'Не поддерживается'}</Text>
                </Space>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label="OpenAI Whisper"
                span={1}
              >
                <Space>
                  <StatusIcon success={sttCapabilities.openaiWhisper} />
                  <Text>{sttCapabilities.openaiWhisper ? 'Настроен' : 'Не настроен'}</Text>
                </Space>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label="Транскрипция в реальном времени"
                span={1}
              >
                <Space>
                  <StatusIcon success={sttCapabilities.realtimeTranscription} />
                  <Text>{sttCapabilities.realtimeTranscription ? 'Доступна' : 'Недоступна'}</Text>
                </Space>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label="Транскрипция файлов"
                span={1}
              >
                <Space>
                  <StatusIcon success={sttCapabilities.fileTranscription} />
                  <Text>{sttCapabilities.fileTranscription ? 'Доступна' : 'Недоступна'}</Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="AI/LLM конфигурация" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item 
                label="Провайдер"
                span={1}
              >
                <Tag color={aiCapabilities.provider !== 'не настроен' ? 'green' : 'red'}>
                  {aiCapabilities.provider}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label="OpenAI API"
                span={1}
              >
                <Space>
                  <StatusIcon success={aiCapabilities.openaiConfigured} />
                  <Text>{aiCapabilities.openaiConfigured ? 'Настроен' : 'Не настроен'}</Text>
                </Space>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label="Ollama URL"
                span={1}
              >
                <Text code>{aiCapabilities.ollamaUrl}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Card title="Тестирование AI сервисов" style={{ marginTop: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button 
            type="primary" 
            loading={testing}
            onClick={testAIService}
            disabled={aiCapabilities.provider === 'не настроен'}
          >
            Тестировать AI сервисы
          </Button>

          {Object.keys(testResults).length > 0 && (
            <div>
              <Title level={4}>Результаты тестирования:</Title>
              
              {testResults.petitionGeneration && (
                <Alert
                  type={testResults.petitionGeneration.success ? 'success' : 'error'}
                  message="Генерация петиций"
                  description={
                    testResults.petitionGeneration.success ? (
                      <div>
                        <Text strong>Заголовок:</Text> {testResults.petitionGeneration.title}<br/>
                        <Text strong>Описание:</Text> {testResults.petitionGeneration.description}
                      </div>
                    ) : (
                      testResults.petitionGeneration.error
                    )
                  }
                  style={{ marginBottom: 8 }}
                />
              )}

              {testResults.moderation && (
                <Alert
                  type={testResults.moderation.success ? 'success' : 'error'}
                  message="Модерация контента"
                  description={
                    testResults.moderation.success ? (
                      <div>
                        <Badge 
                          status={testResults.moderation.is_safe ? 'success' : 'error'} 
                          text={testResults.moderation.is_safe ? 'Контент безопасен' : 'Обнаружены проблемы'} 
                        />
                        {testResults.moderation.issues.length > 0 && (
                          <div>Проблемы: {testResults.moderation.issues.join(', ')}</div>
                        )}
                      </div>
                    ) : (
                      testResults.moderation.error
                    )
                  }
                />
              )}
            </div>
          )}
        </Space>
      </Card>

      <Alert
        type="info"
        message="Инструкции по настройке"
        description={
          <div>
            <Text>Для полной функциональности настройте AI провайдер в файле .env:</Text>
            <ul>
              <li><Text code>REACT_APP_AI_PROVIDER=openai</Text> или <Text code>ollama</Text></li>
              <li><Text code>REACT_APP_OPENAI_API_KEY=ваш_ключ</Text> (для OpenAI)</li>
              <li>Или установите Ollama локально для бесплатного использования</li>
            </ul>
            <Text>Подробности в файле AI_SETUP.md</Text>
          </div>
        }
        style={{ marginTop: 16 }}
      />
    </div>
  );
};

export default AISettingsPage;